import React, { useState } from 'react';
import _ from 'lodash';
import { Board } from '@gorilla/common/src/lib/monday-api/api';
import { getSortableFields, areBoardFiltersValid, BoardFilters } from '@gorilla/common/src/lib/rules';
import statusColumnHandler from '@gorilla/common/src/lib/engine/column_handlers/status_column_handler';
import dropdownColumnHandler from '@gorilla/common/src/lib/engine/column_handlers/dropdown_column_handler';
import { Heading } from '@vibe/core';
import { dropdownValue } from './dropdownValue';
import { AppModal, AppModalContent } from './AppModal';
import { Board as BoardIcon, Duplicate, Info } from '@vibe/icons';
import { Button, Box, Flex, TextField, Text, Divider, Icon, Tooltip, Dropdown } from '@vibe/core';
import {
  Rule,
  Rules,
  BLANK_STATUS_INDEX,
  FilterableField,
  getFilterableFields,
  OPERATORS,
  COMPARE_ATTRIBUTES,
  COMPARE_VALUES,
  validateRule,
  getNormalizedBoardFilters,
} from '@gorilla/common/src/lib/rules';

type StringValueProps = {
  value: any;
  board: Board;
  columnId: string;
  description?: string;
  onChange: (newValue: string) => void;
};

function replaceDangerousChars(str: string) {
  return str.replace(/\\/g, '');
}

function StringValue({ value, onChange, description }: StringValueProps) {
  return (
    <div style={{ flex: 1 }}>
      <div style={{ width: '100%' }}>
        <Tooltip content={description} position="left" zIndex={100000}>
          <TextField
            size="small"
            value={replaceDangerousChars(value || '')}
            onChange={(newValue) => {
              onChange(replaceDangerousChars(newValue || ''));
            }}
            validation={
              {
                //text: description,
              }
            }
            required
            //iconName={description ? LearnMoreIcon : ''}
          />
        </Tooltip>
      </div>
    </div>
  );
}

type NumberValueProps = {
  value: any;
  board: Board;
  columnId: string;
  description?: string;
  onChange: (newValue: number | null) => void;
};

function NumberValue({ value, onChange, description }: NumberValueProps) {
  return (
    <div style={{ flex: 1 }}>
      <div style={{ width: '100%' }}>
        <Tooltip content={description} position="top" zIndex={100000}>
          <TextField
            size="small"
            value={value || ''}
            onChange={(newValue) => {
              onChange(newValue === '' ? null : +newValue);
            }}
            validation={
              {
                //text: description,
              }
            }
            type="number"
            required
            //iconName={description ? LearnMoreIcon : ''}
          />
        </Tooltip>
      </div>
    </div>
  );
}

type StringPairValueProps = {
  value: any;
  board: Board;
  columnId: string;
  description?: string;
  onChange: (newValue: [string, string]) => void;
};

function StringPairValue({ value, onChange, description }: StringPairValueProps) {
  const normalizedValue = _.isArray(value) && value.length === 2 ? value : ['', ''];

  return (
    <div style={{ display: 'flex', gap: '10px' }}>
      <div style={{ flex: 1 }}>
        <div style={{ width: '100%' }}>
          <Tooltip content={description} position="top" zIndex={100000}>
            <TextField
              size="small"
              value={replaceDangerousChars(normalizedValue[0] || '')}
              onChange={(newValue) => {
                onChange([replaceDangerousChars(newValue), replaceDangerousChars(normalizedValue[1])]);
              }}
              validation={
                {
                  //text: description,
                }
              }
              required
              //iconName={description ? LearnMoreIcon : ''}
            />
          </Tooltip>
        </div>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ width: '100%' }}>
          <Tooltip content={description} position="top" zIndex={100000}>
            <TextField
              size="small"
              value={replaceDangerousChars(normalizedValue[1] || '')}
              onChange={(newValue) => {
                onChange([replaceDangerousChars(normalizedValue[0]), replaceDangerousChars(newValue)]);
              }}
              validation={
                {
                  //text: description,
                }
              }
              required
              //iconName={description ? LearnMoreIcon : ''}
            />
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

type StatusValueProps = {
  value: any;
  board: Board;
  columnId: string;
  description?: string;
  onChange: (newValue: number[]) => void;
};

// the rule stores label ids rather than label text, and two labels can read alike, so show the id
// next to the label. Call it "ID", not "index": monday's StatusLabel.index is the label's display
// position and changes when labels are reordered, while the number we store is StatusLabel.id,
// which is assigned at creation and stable. The legacy `{"index": n}` column value means the id.
function getStatusOptionLabel(label: string, id: number) {
  return `${label} (ID ${id})`;
}

// a status rule holds an array of label ids, but rules created before multi-select hold a single id
function getSelectedStatusIndexes(value: any): number[] {
  if (_.isArray(value)) return value.filter((entry) => _.isNumber(entry));
  if (_.isNumber(value)) return [value];
  return [];
}

export function StatusValue({ columnId, board, value, onChange }: StatusValueProps) {
  const statusColumn = board.columns.find((column) => column.id === columnId);
  const selectedIndexes = getSelectedStatusIndexes(value);
  let options: { value: number; label: string }[] = [];

  if (statusColumn?.type === 'status' && statusColumn.settings_str) {
    const settings = statusColumnHandler.convertSettingsToEngine!(statusColumn.settings_str);

    if (_.isObject(_.get(settings, 'labels'))) {
      options = Object.entries(settings.labels).map(([key, value]) => ({ value: +key, label: getStatusOptionLabel(value, +key) }));
    }

    // the empty status is filterable but isn't one of the column's labels, so offer it explicitly
    if (!options.find((option) => option.value === BLANK_STATUS_INDEX)) {
      options.push({ value: BLANK_STATUS_INDEX, label: getStatusOptionLabel('Blank', BLANK_STATUS_INDEX) });
    }

    for (const selectedIndex of selectedIndexes) {
      if (!options.find((option) => option.value === selectedIndex)) {
        options.push({ value: selectedIndex, label: getStatusOptionLabel('Unknown', selectedIndex) });
      }
    }
  }

  const selectedOptions = selectedIndexes.flatMap((selectedIndex) => options.filter((option) => option.value === selectedIndex));

  return (
    <div style={{ flex: 1 }}>
      <div style={{ width: '100%' }}>
        <Dropdown
          size="small"
          options={options}
          value={selectedOptions}
          placeholder="Select Status"
          clearable={false}
          multi
          multiline={false}
          onChange={(newStatuses) => {
            // in multi mode the dropdown reports an array, and null once the last status is removed
            onChange(_.castArray(newStatuses || []).map((newStatus: { value: number }) => newStatus.value));
          }}
        />
      </div>
    </div>
  );
}

type GroupValueProps = {
  value: any;
  board: Board;
  columnId: string;
  description?: string;
  onChange: (newValue: string) => void;
};

function GroupValue({ board, value, onChange }: GroupValueProps) {
  let options: { value: string; label: string }[] = (board.groups || []).map((group) => ({ value: group.id, label: group.title }));

  if (value && !options.find((option) => option.value === value)) {
    // group doesn't exist anymore
    options.push({ value, label: `Unknown (${value})` });
  }

  const selectedOption = options.find((option) => option.value === value);

  return (
    <div style={{ flex: 1 }}>
      <div style={{ width: '100%' }}>
        <Dropdown
          size="small"
          options={options}
          value={dropdownValue(selectedOption)}
          placeholder="Select Group"
          clearable={false}
          multi={false}
          onChange={(newGroup) => {
            onChange(newGroup.value);
          }}
        />
      </div>
    </div>
  );
}

function getCompareValueDescription(defaultDescription: string, board: Board, columnId: string | undefined, compareValueId: string) {
  if (!columnId) {
    return defaultDescription;
  }

  const column = board.columns.find((col) => col.id === columnId);

  if (!column) {
    return defaultDescription;
  }

  try {
    if (compareValueId === 'label_id' && column.type === 'dropdown' && column.settings_str) {
      const settings = dropdownColumnHandler.convertSettingsToEngine!(column.settings_str);

      // @ts-ignore
      if (Array.isArray(settings?.labels)) {
        // @ts-ignore
        return `Available labels: ${settings.labels.length ? settings.labels.map((label) => `${label.name} (ID=${label.id})`).join(', ') : 'None'}`;
      }
    }
  } catch (err) {}

  return defaultDescription;
}

const compareValueComponents = {
  string: StringValue,
  number: NumberValue,
  string_pair: StringPairValue,
  status: StatusValue,
  group: GroupValue,
};

type RuleRowProps = {
  board: Board;
  filterableFields: FilterableField[];
  partialRule: PartialRule;
  showErrors?: boolean;
  onChange: (partialRule: PartialRule) => void;
};

function RuleRow({ board, filterableFields, partialRule, onChange, showErrors }: RuleRowProps) {
  const fieldOptions = filterableFields.map((filterableField) => ({
    value: filterableField.id,
    label: filterableField.title,
  }));
  const selectedField = filterableFields.find((filterableField) => filterableField.id === partialRule.column_id);
  const selectedFieldOption = selectedField ? { value: selectedField.id, label: selectedField.title } : undefined;
  const showOperatorDropdown = !!selectedField;
  const validateMessage = validateRule(board, partialRule);

  const operatorOptions = (selectedField?.supported_rules || []).map((supportedRule) => ({
    label: OPERATORS[supportedRule.operator]?.label || supportedRule.operator,
    value: supportedRule.operator,
  }));

  const selectedOperatorOption = operatorOptions.find((operatorOption) => operatorOption.value === partialRule.operator);
  const supportedRule = selectedField?.supported_rules.find((supportedRule) => supportedRule.operator === partialRule.operator);
  const ruleHasAttributes =
    supportedRule &&
    'compare_attributes_ids' in supportedRule &&
    supportedRule.compare_attributes_ids &&
    supportedRule.compare_attributes_ids.length;
  const showAttributesDropdown = ruleHasAttributes;

  const attributeOptions = (supportedRule?.compare_attributes_ids || []).map((attributeId) => {
    return {
      label: COMPARE_ATTRIBUTES[attributeId].label || attributeId,
      value: attributeId,
    };
  });

  const selectedAttributeOption = attributeOptions.find((attributeOption) => attributeOption.value === partialRule.compare_attribute_id);
  const isAttributesDropownValid = ruleHasAttributes ? !!selectedAttributeOption : true;
  const showCompareValueDropdown =
    isAttributesDropownValid &&
    supportedRule &&
    'compare_value_ids' in supportedRule &&
    supportedRule.compare_value_ids &&
    supportedRule.compare_value_ids.length;

  const compareValueOptions = (supportedRule?.compare_value_ids || []).map((compareValueId) => {
    return {
      label: COMPARE_VALUES[compareValueId].label || compareValueId,
      value: compareValueId,
    };
  });

  const selectedCompareValueOption = compareValueOptions.find(
    (compareValueOption) => compareValueOption.value === _.get(partialRule, 'compare_value.0.id'),
  );

  let compareValueComp: React.ReactNode | null = null;

  if (
    selectedCompareValueOption &&
    'type' in COMPARE_VALUES[selectedCompareValueOption.value] &&
    // @ts-ignore
    compareValueComponents[COMPARE_VALUES[selectedCompareValueOption.value]['type']]
  ) {
    // @ts-ignore
    const CompareValueComponent = compareValueComponents[COMPARE_VALUES[selectedCompareValueOption.value]['type']];

    compareValueComp = (
      <CompareValueComponent
        columnId={partialRule.column_id}
        board={board}
        value={_.get(partialRule, 'compare_value.0.value')}
        onChange={(newValue: any) => {
          onChange({
            ...partialRule,
            compare_value: [{ id: selectedCompareValueOption.value, value: newValue }],
          });
        }}
        description={getCompareValueDescription(
          // @ts-ignore
          COMPARE_VALUES[selectedCompareValueOption.value]['description'] || '',
          board,
          partialRule.column_id,
          selectedCompareValueOption.value,
        )}
      />
    );
  }

  return (
    <>
      <div style={{ display: 'flex', gap: '10px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ width: '100%' }}>
            <Dropdown
              size="small"
              options={fieldOptions}
              value={dropdownValue(selectedFieldOption)}
              placeholder="Select Column"
              clearable={false}
              multi={false}
              onChange={(newField) => {
                onChange(
                  _.omit(
                    {
                      ...partialRule,
                      column_id: newField.value,
                    },
                    ['operator', 'compare_attribute_id', 'compare_value'],
                  ),
                );
              }}
            />
          </div>
        </div>
        {showOperatorDropdown && (
          <div style={{ flex: 1 }}>
            <div style={{ width: '100%' }}>
              <Dropdown
                size="small"
                key={partialRule.column_id}
                options={operatorOptions}
                value={dropdownValue(selectedOperatorOption)}
                placeholder="Select Operator"
                clearable={false}
                multi={false}
                onChange={(newOperator) => {
                  onChange(
                    _.omit(
                      {
                        ...partialRule,
                        operator: newOperator.value,
                      },
                      ['compare_attribute_id', 'compare_value'],
                    ),
                  );
                }}
              />
            </div>
          </div>
        )}
        {showAttributesDropdown && (
          <div style={{ flex: 1 }}>
            <div style={{ width: '100%' }}>
              <Dropdown
                size="small"
                key={partialRule.operator}
                options={attributeOptions}
                value={dropdownValue(selectedAttributeOption)}
                placeholder="Select Attribute"
                clearable={false}
                multi={false}
                onChange={(newAttribute) => {
                  onChange(
                    _.omit(
                      {
                        ...partialRule,
                        compare_attribute_id: newAttribute.value,
                      },
                      ['compare_value'],
                    ),
                  );
                }}
              />
            </div>
          </div>
        )}
        {showCompareValueDropdown && (
          <div style={{ flex: 1 }}>
            <div style={{ width: '100%' }}>
              <Dropdown
                size="small"
                key={`${partialRule.operator}__${partialRule.compare_attribute_id || 'none'}`}
                options={compareValueOptions}
                value={dropdownValue(selectedCompareValueOption)}
                placeholder="Select Value"
                clearable={false}
                multi={false}
                onChange={(newCompareValue) => {
                  onChange({
                    ...partialRule,
                    compare_value: [{ id: newCompareValue.value, value: null }],
                  });
                }}
              />
            </div>
          </div>
        )}
        {compareValueComp}
      </div>

      {showErrors && validateMessage && (
        <div style={{ marginTop: '5px', color: '#d83a52' }}>
          <small>{validateMessage.error}</small>
        </div>
      )}
    </>
  );
}

type PartialRule = Partial<Rule>;

// TODO: confirm this slug once the help article is published on the website
const STATUS_LABEL_HELP_URL = 'https://getgorilla.app/resources/status-filters-label-changes';

// monday identifies a status label by its id rather than by its text, and a rule stores that id.
// monday blocks deleting a label while items carry it, but a label no item uses can be deleted, and
// the freed id can then go to a label created later — silently changing what an existing rule
// matches. Filters aren't part of monday's check, so say so as soon as a rule depends on a label.
function hasStatusRule(board: Board, rules: PartialRule[]) {
  return rules.some((rule) => {
    if (rule.operator !== 'any_of' && rule.operator !== 'not_any_of') {
      return false;
    }

    return board.columns.some((column) => column.id === rule.column_id && column.type === 'status');
  });
}

function StatusLabelNote() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        marginBottom: '16px',
        padding: '16px',
        borderRadius: '4px',
        backgroundColor: '#cce5ff',
      }}
    >
      <Icon icon={Info} size={16} style={{ flex: '0 0 auto', transform: 'translate3d(0,2px,0)' }} />
      <Text type="text3" align="start" ellipsis={false}>
        <u>Important:</u> status rules match a label by its <strong>ID</strong>, not by its name. A label no item uses can be deleted, and
        its ID can then be given to a label you create later. This filter would match that new label instead, without any warning.
        Deactivate labels you no longer need rather than deleting them.{' '}
        <a href={STATUS_LABEL_HELP_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#323338', fontSize: '12px' }}>
          <strong>Learn more</strong>
        </a>
      </Text>
    </div>
  );
}

type BoardRulesModalProps = {
  board: Board;
  isOpen: boolean;
  hideOrderBy?: boolean;
  hideCondition?: boolean;
  inititalBoardFilters?: BoardFilters;
  onClose: (newBoardFilters?: BoardFilters) => void;
};

export function BoardRulesModal({ board, isOpen, inititalBoardFilters, onClose, hideOrderBy, hideCondition }: BoardRulesModalProps) {
  const normalizedInititalBoardFilters = getNormalizedBoardFilters(board, inititalBoardFilters) || {};
  const [rules, setRules] = useState<PartialRule[]>(normalizedInititalBoardFilters?.rules || []);
  const [newRule, setNewRule] = useState<PartialRule>({});
  const [condition, setCondition] = useState(normalizedInititalBoardFilters?.operator || 'and');
  const [sortColumnId, setColumnId] = useState(normalizedInititalBoardFilters?.order_by_column_id || null);
  const [sortDirection, setSortDirection] = useState(normalizedInititalBoardFilters?.order_by_direction || null);
  const filterableFields = getFilterableFields(board);
  const sortableFields = getSortableFields(board);
  const allRulesValid = rules.every((rule) => !validateRule(board, rule));
  const showStatusLabelNote = hasStatusRule(board, [...rules, newRule]);
  const newRuleValidation = validateRule(board, newRule);
  const showErrors = !areBoardFiltersValid(board, normalizedInititalBoardFilters);

  const sortableFieldOptions = sortableFields.map((sortableField) => ({
    value: sortableField.id,
    label: sortableField.title,
  }));

  const sortDirectionOptions: { label: string; value: 'asc' | 'desc' }[] = [
    { label: 'Ascending', value: 'asc' },
    { label: 'Descending', value: 'desc' },
  ];

  return (
    <AppModal
      contentSpacing={false}
      alertDialog={true}
      data-testid="board-rules-modal"
      width="900px"
      show={isOpen}
      onClose={() => {
        onClose();
      }}
    >
      <AppModalContent>
        <Flex direction="row" align="center" gap={4} style={{ marginBottom: '24px' }}>
          <Flex gap={8}>
            <Icon icon={BoardIcon} size={20} />
            <div style={{ maxWidth: '200px' }}>
              <Text type="text1" weight="medium" ellipsis>
                Board filters
              </Text>
            </div>
          </Flex>
        </Flex>

        {showStatusLabelNote ? <StatusLabelNote /> : <></>}

        <Box border rounded="small" backgroundColor="allgreyBackgroundColor">
          <div style={{ padding: '16px' }}>
            <div
              style={{
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                justifyContent: 'stretch',
                alignItems: 'center',
                gap: '16px',
                width: '100%',
                height: 'auto',
              }}
            >
              <small>
                <strong>New rule</strong>
              </small>

              <div style={{ display: 'flex', gap: '10px', flex: 1 }}>
                <div style={{ flex: 1 }}>
                  <RuleRow
                    board={board}
                    filterableFields={filterableFields}
                    partialRule={newRule}
                    onChange={(newPartialRule) => {
                      setNewRule(newPartialRule);
                    }}
                  />
                </div>
                <div style={{ flex: 0 }}>
                  <Button
                    disabled={!!newRuleValidation}
                    size="small"
                    onClick={() => {
                      setRules([...rules, newRule]);
                      setNewRule({});
                    }}
                    style={{
                      paddingRight: '12px',
                      paddingLeft: '12px',
                    }}
                  >
                    Add rule
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <Divider withoutMargin />
          <div style={{ overflowY: 'auto', padding: '16px', backgroundColor: '#fff' }}>
            {rules.length > 0 ? (
              <>
                <div
                  style={
                    {
                      //height: 'calc(100vh - 490px)',
                    }
                  }
                >
                  {rules.map((rule, index) => {
                    return (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '10px',
                          marginTop: '10px',
                        }}
                      >
                        <div style={{ marginRight: '10px', width: '30px', fontSize: '14px', lineHeight: '32px', textAlign: 'center' }}>
                          {/*{index+1}*/}
                          {index === 0 ? <>If</> : <>{condition}</>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <RuleRow
                            board={board}
                            filterableFields={filterableFields}
                            partialRule={rule}
                            showErrors={showErrors}
                            onChange={(newPartialRule) => {
                              setRules(
                                rules.map((r, i) => {
                                  return i === index ? newPartialRule : r;
                                }),
                              );
                            }}
                          />
                        </div>
                        <div style={{ marginLeft: 'auto' }}>
                          <Button
                            size="small"
                            kind="tertiary"
                            onClick={() => {
                              // @ts-ignore
                              setRules(rules.filter((r, i) => i !== index));
                            }}
                            style={{
                              paddingRight: '12px',
                              paddingLeft: '12px',
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ height: '10px' }}></div>
                </div>
              </>
            ) : (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '32px 0',
                  //height: 'calc(100vh - 480px)',
                }}
              >
                <Flex direction="column" gap={8}>
                  <Icon icon={Duplicate} size={32} />
                  <Heading type="h3" weight="bold" align="start">
                    No rules created
                  </Heading>
                  <Text align="center">
                    Add a new rule by selecting a column from the <br />
                    dropdown above. Define its conditional <br />
                    operators and values as displayed.
                  </Text>
                </Flex>
              </div>
            )}
          </div>

          <Divider withoutMargin />

          <Box paddingStart="medium" paddingEnd="medium">
            <Flex gap={16}>
              {!hideCondition && (
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      flex: 1,
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '16px',
                      //width: hideOrderBy ? '160px' : '100%',
                      width: '100%',
                      height: '64px',
                    }}
                  >
                    <small>
                      <strong>Condition</strong>
                    </small>
                    <div style={{ marginTop: '0', width: '100%' }}>
                      <Dropdown
                        size="small"
                        options={[
                          { label: 'And', value: 'and' },
                          { label: 'Or', value: 'or' },
                        ]}
                        value={{ label: condition === 'and' ? 'And' : 'Or', value: condition }}
                        placeholder="Select Condition"
                        clearable={false}
                        multi={false}
                        onChange={(newCondition) => {
                          setCondition(newCondition.value);
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              {!hideCondition && !hideOrderBy ? (
                <div style={{ height: '64px' }}>
                  <Divider direction="vertical" withoutMargin />
                </div>
              ) : null}
              {!hideOrderBy && (
                <div
                  style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '16px',
                    width: '100%',
                    height: '64px',
                  }}
                >
                  <small>
                    <strong>Sorting</strong>
                  </small>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '0', width: '100%' }}>
                    <div style={{ width: '100%' }}>
                      <div style={{ width: '100%' }}>
                        <Dropdown
                          size="small"
                          options={sortableFieldOptions}
                          value={dropdownValue(sortableFieldOptions.find((option) => option.value === sortColumnId))}
                          placeholder="No sorting"
                          clearable={true}
                          maxMenuHeight={130}
                          multi={false}
                          onChange={(newColumnId) => {
                            if (newColumnId) {
                              setColumnId(newColumnId.value);
                              if (!sortDirection) {
                                setSortDirection('asc');
                              }
                            } else {
                              setColumnId(null);
                              setSortDirection(null);
                            }
                          }}
                        />
                      </div>
                    </div>
                    {sortColumnId && (
                      <div style={{ flex: 1 }}>
                        <div style={{ width: '130px' }}>
                          <Dropdown
                            size="small"
                            options={sortDirectionOptions}
                            value={dropdownValue(sortDirectionOptions.find((option) => option.value === sortDirection))}
                            placeholder="Select Direction"
                            clearable={false}
                            multi={false}
                            onChange={(newSortDirection) => {
                              setSortDirection(newSortDirection ? newSortDirection.value : null);
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Flex>
          </Box>
        </Box>

        <Flex gap={12} justify="end" style={{ marginTop: '24px' }}>
          <Box marginEnd="auto">
            <Text>
              Create filters on the <Icon icon={BoardIcon} style={{ transform: 'translate3d(0,3px,0)' }} /> <strong>{board.name}</strong>{' '}
              board by adding rules for specific board columns.
            </Text>
          </Box>
          <Button
            kind="secondary"
            size="medium"
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={!allRulesValid}
            size="medium"
            onClick={() => {
              const newBoardFilters: BoardFilters = {};

              if (rules.length) {
                newBoardFilters.rules = rules as Rules;
              }

              if (condition) {
                newBoardFilters.operator = condition;
              }

              if (sortColumnId) {
                newBoardFilters.order_by_column_id = sortColumnId;

                if (sortDirection) {
                  newBoardFilters.order_by_direction = sortDirection;
                }
              }

              onClose(newBoardFilters);
            }}
          >
            Confirm
          </Button>
        </Flex>
      </AppModalContent>
    </AppModal>
  );
}
